import { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, PhoneOff, Volume2, VolumeX, Pause, Play } from 'lucide-react';
import CallControls from './CallControls';
import NotesPanel from './NotesPanel';
import RecordingPlayer from './RecordingPlayer';
import DispositionSelector from './DispositionSelector';
import CallbackScheduler from './CallbackScheduler';
import CallTransfer from './CallTransfer';
import CallScripts from './CallScripts';
import ContactTags from './ContactTags';
import EmailSmsSender from './EmailSmsSender';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function DialerInterface({
  campaign,
  activeCall,
  callStatus,
  socket,
  onCallInitiated,
  onCallEnded
}) {
  const [selectedContact, setSelectedContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialingMode, setDialingMode] = useState(campaign?.dialing_mode || 'preview');

  useEffect(() => {
    if (campaign) {
      fetchContacts();
    }
  }, [campaign, searchTerm]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        status: 'pending',
        limit: '50',
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(
        `${API_BASE}/dialer/campaigns/${campaign.id}/contacts?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDial = async (contact) => {
    if (activeCall) {
      alert('You already have an active call');
      return;
    }

    if (!contact || !contact.id) {
      alert('Invalid contact selected');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        alert('You must be logged in to make calls');
        return;
      }

      console.log('Initiating call to:', contact.full_name, contact.phone);
      
      const response = await axios.post(
        `${API_BASE}/dialer/dial`,
        {
          campaign_id: campaign.id,
          contact_id: contact.id,
          dialing_mode: dialingMode
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 35000 // 35 second timeout
        }
      );

      console.log('Call initiated successfully:', response.data);
      onCallInitiated({ ...response.data, contact });
      setSelectedContact(contact);
    } catch (error) {
      console.error('Error initiating call:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to initiate call';
      alert(`Call failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleHangup = async () => {
    if (!activeCall) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE}/dialer/calls/${activeCall.id}/hangup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCallEnded();
      setSelectedContact(null);
    } catch (error) {
      console.error('Error hanging up:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered':
        return 'text-green-600';
      case 'ringing':
        return 'text-yellow-600';
      case 'initiated':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Dialer</h2>
        <div className="flex items-center gap-4">
          <select
            value={dialingMode}
            onChange={(e) => setDialingMode(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="manual">Manual</option>
            <option value="preview">Preview</option>
          </select>
        </div>
      </div>

      {/* Active Call Display */}
      {activeCall && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-lg">
                {activeCall.contact?.full_name || 'Unknown'}
              </div>
              <div className="text-gray-600">{activeCall.contact?.phone}</div>
              <div className="text-sm mt-1">
                {activeCall.contact?.company_name}
              </div>
            </div>
            <div className="text-right">
              <div className={`font-semibold ${getStatusColor(callStatus)}`}>
                {callStatus || 'Connecting...'}
              </div>
              {callStatus === 'answered' && (
                <div className="text-sm text-gray-600 mt-1">Call in progress</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact List */}
      <div>
        <input
          type="text"
          placeholder="Search contacts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        {loading ? (
          <div className="text-center py-8">Loading contacts...</div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No contacts found</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className={`border rounded p-3 cursor-pointer hover:bg-gray-50 ${
                  selectedContact?.id === contact.id ? 'bg-blue-50 border-blue-300' : ''
                }`}
                onClick={() => setSelectedContact(contact)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{contact.full_name}</div>
                    <div className="text-sm text-gray-600">{contact.phone}</div>
                    {contact.company_name && (
                      <div className="text-sm text-gray-500">{contact.company_name}</div>
                    )}
                  </div>
                  {dialingMode === 'preview' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDial(contact);
                      }}
                      disabled={activeCall || loading}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                      title={activeCall ? 'Call in progress' : `Call ${contact.full_name}`}
                    >
                      <Phone className="w-4 h-4" />
                      {loading ? 'Dialing...' : 'Dial'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Call Controls */}
      {activeCall && (
        <CallControls
          call={activeCall}
          callStatus={callStatus}
          onHangup={handleHangup}
          socket={socket}
        />
      )}

      {/* Notes Panel - Always visible when call is active */}
      {activeCall && (
        <NotesPanel callId={activeCall.id} contactId={activeCall.contact_id} />
      )}

      {/* Disposition Selector */}
      {activeCall && (
        <DispositionSelector 
          callId={activeCall.id} 
          campaignId={campaign.id}
          onDispositionSet={() => {}}
        />
      )}

      {/* Callback Scheduler */}
      {activeCall && (
        <CallbackScheduler
          contactId={activeCall.contact_id}
          campaignId={campaign.id}
          onScheduled={() => {}}
        />
      )}

      {/* Call Transfer */}
      {activeCall && callStatus === 'answered' && (
        <CallTransfer
          callId={activeCall.id}
          onTransferred={() => {}}
        />
      )}

      {/* Call Scripts */}
      {activeCall && (
        <CallScripts
          campaignId={campaign.id}
          callId={activeCall.id}
        />
      )}

      {/* Contact Tags */}
      {activeCall && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2">Contact Tags</h3>
          <ContactTags contactId={activeCall.contact_id} />
        </div>
      )}

      {/* Email/SMS Sender */}
      {activeCall && (
        <EmailSmsSender
          contactId={activeCall.contact_id}
          campaignId={campaign.id}
          callId={activeCall.id}
        />
      )}

      {/* Recording Player */}
      {activeCall && callStatus === 'ended' && (
        <RecordingPlayer callId={activeCall.id} />
      )}
    </div>
  );
}

